import { useCallback, useRef, useState, useEffect } from "react";
import * as faceapi from "face-api.js";
import DetectionCard from "./detectionCard";
import { customLoader } from "../../theme/loader";
import { SearchBar } from "./searchBox";
import { ResultCrad } from "./resultCards";
import SimilarityChips from "./similarityChips";
import { Grid, Box, createStyles, Title, Text, Stack, Paper } from "@mantine/core";
import authAxios from "../../keycloak/authAxios";
import { alertNotify } from "../../Notify/notifyMe";
import { useCookies } from "react-cookie";
import { useStyles as useThemeStyles } from "../../theme/them";

const brandColor = "rgb(42, 155, 73)";

const useStyles = createStyles((theme) => ({
  page: { paddingBottom: 40 },
  header: { textAlign: "center", marginBottom: 24 },
  title: {
    fontSize: 32,
    fontWeight: 700,
    color: theme.colorScheme === "dark" ? theme.white : theme.colors.gray[9],
    marginBottom: 4,
    "@media (max-width: 520px)": { fontSize: 26 },
  },
  subtitle: {
    color: theme.colorScheme === "dark" ? theme.colors.dark[2] : "#868e96",
    fontSize: 16,
  },
  controls: { marginBottom: 24 },
  mainGrid: { alignItems: "stretch" },
  stickyBlock: {
    position: "sticky",
    top: 24,
    width: "100%",
    zIndex: 10,
  },
}));

export const RecognizePersons = () => {
  const { classes } = useStyles();
  const { classes: themeClasses } = useThemeStyles();

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const cameraStream = useRef(null);
  const faceShutterTimer = useRef(null);
  const faceLocationUpdater = useRef(null);
  const [foundPersonResult, setPersonResult] = useState([]);
  const [faceImage, setFaceImage] = useState(null);
  const [canDetecte, setCanDetecae] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [toggle, setToggle] = useState(false);

  const [cookie, setCookie] = useCookies();

  useEffect(() => {
    const MODEL_URL = process.env.PUBLIC_URL + "/models";
    Promise.all([
      faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
      faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
      faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
      faceapi.nets.faceExpressionNet.loadFromUri(MODEL_URL),
    ]);
  }, []);

  const onPlay = () => {
    const canvas = canvasRef.current;
    const displaySize = {
      width: videoRef.current.width,
      height: videoRef.current.height,
    };
    faceapi.matchDimensions(canvas, displaySize);
    faceLocationUpdater.current = setInterval(async () => {
      if (canDetecte === true) {
        const detections = await faceapi.detectAllFaces(
          videoRef.current,
          new faceapi.TinyFaceDetectorOptions()
        );
        const resizedDetections = faceapi.resizeResults(detections, displaySize);
        canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
        faceapi.draw.drawDetections(canvas, resizedDetections);
      }
    }, 50);
  };

  const captureImage = () => {
    if (videoRef.current) {
      const canvas = document.createElement("canvas");
      canvas.width = 400;
      canvas.height = 400;

      const videoWidth = videoRef.current.videoWidth;
      const videoHeight = videoRef.current.videoHeight;

      let sourceX = 0;
      let sourceY = 0;
      let sourceWidth = videoWidth;
      let sourceHeight = videoHeight;

      if (videoWidth > videoHeight) {
        sourceX = (videoWidth - videoHeight) / 2;
        sourceWidth = videoHeight;
      } else if (videoHeight > videoWidth) {
        sourceY = (videoHeight - videoWidth) / 2;
        sourceHeight = videoWidth;
      }

      canvas
        .getContext("2d")
        .drawImage(
          videoRef.current,
          sourceX,
          sourceY,
          sourceWidth,
          sourceHeight,
          0,
          0,
          400,
          400
        );

      setFaceImage(canvas.toDataURL("image/jpeg"));

      setCanDetecae(false);
      stopCamera();
    }
  };

  function startVideo() {
    navigator.mediaDevices
      .getUserMedia({ video: true, audio: false })
      .then(function (stream) {
        cameraStream.current = stream;
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      })
      .catch(function (err) {
        console.log("An error occurred: " + err);
      });
  }

  const stopCamera = () => {
    faceShutterTimer.current = null;
    clearInterval(faceLocationUpdater.current);

    if (videoRef.current) {
      videoRef.current.pause();
    }

    if (cameraStream.current) {
      cameraStream.current.getTracks().forEach(function (track) {
        if (track.readyState === "live") {
          track.stop();
        }
      });
    }
  };

  const searchFromUploadedFile = useCallback((image) => {
    setFaceImage(image);
    snapPhoto();
  }, []);

  const snapPhoto = useCallback(() => {
    if (faceImage == null) {
      return;
    }
    setLoading(true);

    const processImage = (imageData) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = 400;
          canvas.height = 400;

          let sourceX = 0;
          let sourceY = 0;
          let sourceWidth = img.width;
          let sourceHeight = img.height;

          if (img.width > img.height) {
            sourceX = (img.width - img.height) / 2;
            sourceWidth = img.height;
          } else if (img.height > img.width) {
            sourceY = (img.height - img.width) / 2;
            sourceHeight = img.width;
          }

          canvas.getContext("2d").drawImage(
            img,
            sourceX,
            sourceY,
            sourceWidth,
            sourceHeight,
            0,
            0,
            400,
            400
          );

          resolve(canvas.toDataURL("image/jpeg"));
        };
        img.src = imageData;
      });
    };

    processImage(faceImage).then((processedImage) => {
      const bodyFormData = new FormData();
      bodyFormData.append("photo", processedImage);
      bodyFormData.append("similarity", parseFloat(cookie.similarity || "0.95"));
      bodyFormData.append("prediction_count", 50);

      authAxios
        .post(`arshad/recognize`, bodyFormData)
        .then((res) => {
          setToggle(false);

          if (res.data.message === "Person not registered in our system!") {
            alertNotify("لم يتم التعرف على الوجه");
          } else {
            setPersonResult(res.data.result);
            setCanDetecae(false);
          }

          setLoading(false);
        })
        .catch((err) => {
          console.log(err);
          setLoading(false);
          alertNotify("لم يتم التعرف على الوجه");
        });
    });
  }, [faceImage, cookie]);

  useEffect(() => {
    snapPhoto();
  }, [cookie]);

  return (
    <Box className={classes.page}>
      <Box className={classes.header}>
        <Title className={classes.title} order={1}>
          البحث بالتعرف على الوجه
        </Title>
        <Text className={classes.subtitle}>
          التقط أو ارفع صورة للبحث عن الأشخاص المطابقين
        </Text>
      </Box>

      <Paper className={themeClasses.panel} sx={{ marginBottom: 24 }}>
        <SearchBar
          onSearch={(value) => {
            setPersonResult(value);
          }}
        />
      </Paper>

      <Grid gutter={24} className={classes.mainGrid}>
        <Grid.Col md={5}>
          <Box className={classes.stickyBlock}>
            <Stack spacing="md">
              <SimilarityChips setCookie={setCookie} cookie={cookie} />
              <DetectionCard
                toggle={toggle}
                searchFromUploadedFile={searchFromUploadedFile}
                setToggle={setToggle}
                setCanDetecae={setCanDetecae}
                setFaceImage={setFaceImage}
                startVideo={startVideo}
                isLoading={isLoading}
                customLoader={customLoader}
                onPlay={onPlay}
                videoRef={videoRef}
                canvasRef={canvasRef}
                faceImage={faceImage}
                snapPhoto={snapPhoto}
                stopCamera={stopCamera}
                captureImage={captureImage}
                faceapi={faceapi}
              />
            </Stack>
          </Box>
        </Grid.Col>

        <Grid.Col md={7}>
          <Paper className={themeClasses.panel} sx={{ height: "100%" }}>
            <ResultCrad foundPersonResult={foundPersonResult} toggle={toggle} />
          </Paper>
        </Grid.Col>
      </Grid>
    </Box>
  );
};
